/**
 * Markdown → HTML for popup content authored as an `.md` file.
 *
 * Deliberately small rather than a dependency: the pages this renders (game
 * rules, paytables, terms) use headings, paragraphs, emphasis, lists, tables,
 * rules and links, so that is what it covers. Raw HTML lines pass straight
 * through, which is the escape hatch for anything richer.
 *
 * The input is first-party content shipped with the game, so it is trusted —
 * only the inside of code spans and fences is escaped, and that is to keep the
 * markup readable rather than to sanitise it. Do not point this at text a player
 * can write.
 */

const HEADING = /^ {0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const FENCE = /^ {0,3}(```|~~~)\s*([\w-]*)\s*$/;
const RULE = /^ {0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/;
const QUOTE = /^ {0,3}> ?(.*)$/;
const BULLET = /^(\s*)[-*+]\s+(.*)$/;
const NUMBER = /^(\s*)(\d+)[.)]\s+(.*)$/;
const HTML_BLOCK = /^\s*<\/?[a-zA-Z][^>]*>/;
/** A `|---|:--:|` row, which is what turns the line above it into a table header. */
const TABLE_DIVIDER = /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/;

type OpenList = { tag: 'ul' | 'ol'; indent: number; itemOpen: boolean };

export type MarkdownOptions = {
    /**
     * Resolves a symbol name to an image source. A code span holding nothing but a name this
     * answers — `` `H1` `` — is rendered as that image instead of as code, which is how the
     * rules page shows the game's own symbol art where its text names a symbol. Names it does
     * not answer stay code spans, so a page can still write one as text.
     *
     * The code span is the marker on purpose: `WI` in the middle of a word, or a heading that
     * happens to read `L1`, must not turn into a picture.
     */
    symbolImage?: (name: string) => string | undefined;
};

/** Class on the `<img>` a symbol code span renders as, so the page can size and align them. */
export const SYMBOL_IMAGE_CLASS = 'symbol';

/** Converts a Markdown document to an HTML fragment. */
export function markdownToHtml(markdown: string, options: MarkdownOptions = {}): string {
    const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
    const html: string[] = [];
    const lists: OpenList[] = [];
    let paragraph: string[] = [];
    let quote: string[] | null = null;
    let fence: { marker: string; language: string; code: string[] } | null = null;

    const closeParagraph = () => {
        if (!paragraph.length) return;
        html.push(`<p>${inline(paragraph.join('\n'), options)}</p>`);
        paragraph = [];
    };

    const closeQuote = () => {
        if (!quote) return;
        // recursed, so a quote can hold any block content — lists and all
        html.push(`<blockquote>${markdownToHtml(quote.join('\n'), options)}</blockquote>`);
        quote = null;
    };

    /** Closes every list nested deeper than `indent` (all of them at `-1`). */
    const closeLists = (indent = -1) => {
        while (lists.length && lists[lists.length - 1].indent > indent) {
            const list = lists.pop()!;
            if (list.itemOpen) html.push('</li>');
            html.push(`</${list.tag}>`);
        }
    };

    const closeBlocks = () => {
        closeParagraph();
        closeQuote();
        closeLists();
    };

    for (let index = 0; index < lines.length; index++) {
        const line = lines[index];

        if (fence) {
            if (line.trim() === fence.marker) {
                const attribute = fence.language ? ` class="language-${fence.language}"` : '';
                html.push(`<pre><code${attribute}>${escapeText(fence.code.join('\n'))}</code></pre>`);
                fence = null;
            } else {
                fence.code.push(line);
            }
            continue;
        }

        const fenceStart = line.match(FENCE);
        if (fenceStart) {
            closeBlocks();
            fence = { marker: fenceStart[1], language: fenceStart[2], code: [] };
            continue;
        }

        const quoted = line.match(QUOTE);
        if (quoted) {
            closeParagraph();
            closeLists();
            (quote ??= []).push(quoted[1]);
            continue;
        }
        closeQuote();

        if (!line.trim()) {
            closeBlocks();
            continue;
        }

        if (RULE.test(line)) {
            closeBlocks();
            html.push('<hr>');
            continue;
        }

        const heading = line.match(HEADING);
        if (heading) {
            closeBlocks();
            const level = heading[1].length;
            html.push(`<h${level}>${inline(heading[2], options)}</h${level}>`);
            continue;
        }

        // a `|` row followed by a `|---|` divider — everything up to the next
        // non-`|` line belongs to the table
        if (line.includes('|') && TABLE_DIVIDER.test(lines[index + 1] ?? '')) {
            closeBlocks();
            const rows: string[] = [];
            while (index + 1 < lines.length && (lines[index + 1] ?? '').includes('|')) {
                index++;
                rows.push(lines[index]);
            }
            html.push(table(line, rows[0], rows.slice(1), options));
            continue;
        }

        const bullet = line.match(BULLET);
        const numbered = line.match(NUMBER);
        if (bullet || numbered) {
            closeParagraph();
            const indent = (bullet ? bullet[1] : numbered![1]).length;
            const content = bullet ? bullet[2] : numbered![3];
            openItem(html, lists, closeLists, bullet ? 'ul' : 'ol', indent, content, options);
            continue;
        }

        // an indented line under an open list item continues that item, which is
        // how a wrapped bullet stays one bullet
        if (lists.length && /^\s{2,}\S/.test(line) && lists[lists.length - 1].itemOpen) {
            html[html.length - 1] += ` ${inline(line.trim(), options)}`;
            continue;
        }

        if (HTML_BLOCK.test(line)) {
            closeBlocks();
            html.push(line);
            continue;
        }

        closeLists();
        // trailing spaces survive the trim — two of them are Markdown's hard break
        paragraph.push(line.trimStart());
    }

    if (fence) {
        html.push(`<pre><code>${escapeText(fence.code.join('\n'))}</code></pre>`);
    }
    closeBlocks();

    return html.join('\n');
}

/**
 * Pulls the document's first `# heading` off the top, so a page can use it as its
 * own title (a popup header, a document title) instead of repeating it in the body.
 * Returns the heading and the remaining Markdown.
 */
export function splitLeadingHeading(markdown: string): { title?: string; body: string } {
    const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
    const index = lines.findIndex((line) => line.trim());
    const heading = index === -1 ? null : lines[index].match(/^ {0,3}#\s+(.*?)\s*#*\s*$/);

    if (!heading) return { body: markdown };

    return { title: heading[1], body: lines.slice(index + 1).join('\n') };
}

/**
 * Opens a list item, growing or unwinding the nesting stack to match its indent.
 * A deeper item opens a new list *inside* the currently open `<li>`, which is what
 * makes nested bullets render as nested lists.
 */
function openItem(
    html: string[],
    lists: OpenList[],
    closeLists: (indent?: number) => void,
    tag: 'ul' | 'ol',
    indent: number,
    content: string,
    options: MarkdownOptions,
) {
    closeLists(indent);

    let current = lists[lists.length - 1];

    if (current && current.indent === indent && current.tag !== tag) {
        // same level, different kind of list — end the old one
        if (current.itemOpen) html.push('</li>');
        html.push(`</${current.tag}>`);
        lists.pop();
        current = lists[lists.length - 1];
    }

    if (!current || current.indent < indent) {
        html.push(`<${tag}>`);
        lists.push({ tag, indent, itemOpen: false });
        current = lists[lists.length - 1];
    }

    if (current.itemOpen) html.push('</li>');
    html.push(`<li>${inline(content, options)}`);
    current.itemOpen = true;
}

/** Builds a `<table>` from its header row, its `|---|` divider (for alignment) and its body rows. */
function table(header: string, divider: string, body: string[], options: MarkdownOptions): string {
    const alignments = cells(divider).map((cell) => {
        if (cell.startsWith(':') && cell.endsWith(':')) return 'center';
        if (cell.endsWith(':')) return 'right';
        if (cell.startsWith(':')) return 'left';
        return '';
    });

    const row = (source: string, tag: 'th' | 'td') =>
        `<tr>${cells(source)
            .map((cell, column) => {
                const align = alignments[column] ? ` style="text-align:${alignments[column]}"` : '';
                return `<${tag}${align}>${inline(cell, options)}</${tag}>`;
            })
            .join('')}</tr>`;

    const head = `<thead>${row(header, 'th')}</thead>`;
    const rows = body.map((source) => row(source, 'td')).join('');

    return `<table>${head}${rows ? `<tbody>${rows}</tbody>` : ''}</table>`;
}

function cells(row: string): string[] {
    return row
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());
}

/**
 * Applies the inline (span-level) markup.
 *
 * Code spans are lifted out first and put back last: their contents are literal,
 * so `` `**not bold**` `` must survive the emphasis pass untouched. Putting them
 * back is also where a span that names a symbol becomes that symbol's image (see
 * {@link MarkdownOptions.symbolImage}) — by then the surrounding markup is done,
 * so an `src` full of base64 cannot be mistaken for emphasis.
 */
function inline(text: string, options: MarkdownOptions): string {
    const code: string[] = [];
    // a NUL cannot occur in the source text, so it can stand in for a lifted span
    const PLACEHOLDER = '\u0000';

    let out = text.replace(/`+([^`]+)`+/g, (_, content: string) => {
        code.push(content);
        return `${PLACEHOLDER}${code.length - 1}${PLACEHOLDER}`;
    });

    out = out
        .replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, '<img src="$2" alt="$1">')
        .replace(
            /\[([^\]]+)\]\(([^)\s]+)\)/g,
            '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
        )
        // `(?=\S)` opens the span on a non-space and the lazy `[\s\S]*?\S` closes it
        // on the nearest one, so `**a** and **b**` is two spans rather than one
        .replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '<strong>$2</strong>')
        // guarded on both sides so `snake_case_words` and `2 * 3 * 4` are left alone
        .replace(/(^|[^\w*_])([*_])(?=\S)([\s\S]*?\S)\2(?![\w*_])/g, '$1<em>$3</em>')
        .replace(/~~(?=\S)([\s\S]*?\S)~~/g, '<del>$1</del>')
        // a hard break: two trailing spaces, or the newline of a wrapped paragraph
        .replace(/ {2,}\n/g, '<br>\n')
        .replace(/\n/g, ' ');

    return out.replace(
        new RegExp(`${PLACEHOLDER}(\\d+)${PLACEHOLDER}`, 'g'),
        (_, index: string) => {
            const content = code[Number(index)];
            const image = options.symbolImage?.(content.trim());

            if (image) {
                return `<img class="${SYMBOL_IMAGE_CLASS}" src="${escapeAttribute(image)}" alt="${escapeAttribute(content.trim())}">`;
            }

            return `<code>${escapeText(content)}</code>`;
        },
    );
}

function escapeText(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttribute(text: string): string {
    return escapeText(text).replace(/"/g, '&quot;');
}
